// ─── Recherche globale (story 9.2) — fonctions pures, sans dépendance ────────
import type { Campaign, CampaignStatus, FormTemplate, SupplierListItem } from '@/lib/api'
import type { ChatConversationSummary } from '@/lib/chatTypes'
import { formatConversationDate } from '@/lib/chatFormat'

export type SearchKind = 'supplier' | 'campaign' | 'template' | 'conversation'

export type SearchTarget = { kind: SearchKind; id: string }

export type SearchEntry = {
  kind: SearchKind
  id: string
  label: string
  secondary?: string
  /** Champs normalisés ; le premier est le champ principal. */
  haystack: string[]
  sortDate: string | null
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Brouillon',
  SCHEDULED: 'Programmée',
  ACTIVE: 'Active',
  CLOSED: 'Clôturée',
  ARCHIVED: 'Archivée',
}

export const SEARCH_KINDS: SearchKind[] = ['supplier', 'campaign', 'template', 'conversation']

export function normalizeSearchText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/\s+/g, ' ')
    .trim()
}

function norm(values: Array<string | number | null | undefined>): string[] {
  return values.map((v) => (v === null || v === undefined ? '' : normalizeSearchText(String(v))))
}

export function toSupplierEntries(suppliers: SupplierListItem[]): SearchEntry[] {
  return suppliers.map((s) => ({
    kind: 'supplier',
    id: s.id,
    label: s.name,
    secondary: s.company || s.email,
    haystack: norm([s.name, s.company, s.email]),
    sortDate: s.lastActivityAt ?? s.updatedAt ?? null,
  }))
}

export function toCampaignEntries(campaigns: Campaign[]): SearchEntry[] {
  return campaigns.map((c) => ({
    kind: 'campaign',
    id: c.id,
    label: c.name,
    secondary: CAMPAIGN_STATUS_LABELS[c.status] ?? c.status,
    haystack: norm([c.name, c.category, c.year]),
    sortDate: c.updatedAt ?? null,
  }))
}

export function toTemplateEntries(templates: FormTemplate[]): SearchEntry[] {
  return templates.map((t) => ({
    kind: 'template',
    id: t.id,
    label: t.name,
    secondary: t.isSystem ? 'Système' : (t.category ?? undefined),
    haystack: norm([t.name, t.category]),
    sortDate: t.updatedAt ?? null,
  }))
}

export function toConversationEntries(conversations: ChatConversationSummary[]): SearchEntry[] {
  return conversations.map((c) => {
    const label = c.title?.trim() ? c.title : 'Nouvelle conversation'
    return {
      kind: 'conversation',
      id: c.id,
      label,
      secondary: formatConversationDate(c.updatedAt) || undefined,
      haystack: norm([label]),
      sortDate: c.updatedAt ?? null,
    }
  })
}

function isSubsequence(token: string, text: string): boolean {
  let i = 0
  for (let j = 0; j < text.length && i < token.length; j++) {
    if (text[j] === token[i]) i++
  }
  return i === token.length
}

/**
 * 0 préfixe/exact, 1 préfixe de mot, 2 sous-chaîne, 3 sous-séquence, null sinon.
 * La sous-séquence (fuzzy) n'est autorisée que sur le champ principal (nom/titre).
 */
function matchLevel(token: string, text: string, allowFuzzy: boolean): number | null {
  if (!text) return null
  if (text.startsWith(token)) return 0
  if (text.split(/[^\p{L}\p{N}]+/u).some((w) => w.startsWith(token))) return 1
  if (text.includes(token)) return 2
  if (allowFuzzy && token.length >= 3 && isSubsequence(token, text)) return 3
  return null
}

function scoreEntry(entry: SearchEntry, tokens: string[]): number | null {
  let total = 0
  for (const token of tokens) {
    let best: number | null = null
    entry.haystack.forEach((text, idx) => {
      const level = matchLevel(token, text, idx === 0)
      if (level === null) return
      const s = level * 2 + (idx > 0 ? 1 : 0)
      if (best === null || s < best) best = s
    })
    if (best === null) return null
    total += best
  }
  return total
}

function byRecency(a: SearchEntry, b: SearchEntry): number {
  return (b.sortDate ?? '').localeCompare(a.sortDate ?? '')
}

export function searchEntries(
  entries: SearchEntry[],
  query: string,
  options: { limitPerKind?: number; emptyLimitPerKind?: number } = {}
): Record<SearchKind, SearchEntry[]> {
  const { limitPerKind = 5, emptyLimitPerKind = 3 } = options
  const result: Record<SearchKind, SearchEntry[]> = {
    supplier: [],
    campaign: [],
    template: [],
    conversation: [],
  }
  const tokens = normalizeSearchText(query).split(' ').filter(Boolean)

  if (tokens.length === 0) {
    for (const kind of SEARCH_KINDS) {
      result[kind] = entries
        .filter((e) => e.kind === kind)
        .sort(byRecency)
        .slice(0, emptyLimitPerKind)
    }
    return result
  }

  for (const kind of SEARCH_KINDS) {
    const scored: Array<{ entry: SearchEntry; score: number }> = []
    for (const entry of entries) {
      if (entry.kind !== kind) continue
      const score = scoreEntry(entry, tokens)
      if (score !== null) scored.push({ entry, score })
    }
    scored.sort((a, b) => a.score - b.score || byRecency(a.entry, b.entry))
    result[kind] = scored.slice(0, limitPerKind).map((s) => s.entry)
  }
  return result
}
